import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react'
import useAuthenticatedAxios from '../../../auth/AuthenticatedAxios';
import { useToast } from '../../../Context/ToastContext';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import financeTemplates from '../../../Data/financeTemplateSearch.json';
import personalTemplates from '../../../Data/personalTemplateSearch.json';
import healthTemplates from '../../../Data/healthTemplateSearch.json';
import shoppingTemplates from '../../../Data/shoppingTemplateSearch.json';
import immigrationTemplates from '../../../Data/immigrationTemplateSearch.json';

// Import new template files
import financialAdvisorsTemplates from '../../../Data/financialAdvisorsTemplateSearch.json';
import accountantsTemplates from '../../../Data/accountantsTemplateSearch.json';
import taxAdvisorsTemplates from '../../../Data/taxAdvisorsTemplateSearch.json';
import businessConsultantsTemplates from '../../../Data/businessConsultantsTemplateSearch.json';
import legalAdvisorsTemplates from '../../../Data/legalAdvisorsTemplateSearch.json';
import careerCoachesTemplates from '../../../Data/careerCoachesTemplateSearch.json';
import travelAdvisorsTemplates from '../../../Data/travelAdvisorsTemplateSearch.json';
import itConsultantsTemplates from '../../../Data/itConsultantsTemplateSearch.json';
import realEstateAgentsTemplates from '../../../Data/realEstateAgentsTemplateSearch.json';
import { FALLBACK_TAXONOMY, findCategory } from '../../../Data/advisoryTaxonomy';

// Template category mapping
const TEMPLATE_CATEGORIES = {
    // Existing categories
    FINANCIAL: 'financial',
    PERSONAL: 'personal',
    HEALTH: 'health',
    SHOPPING: 'shopping',
    STARTUP: 'startup',
    IMMIGRATION: 'immigration',
    
    // New Financial & Business categories
    FINANCIAL_ADVISORS: 'financial_advisors',
    ACCOUNTANTS: 'accountants',
    TAX_ADVISORS: 'tax_advisors',
    BUSINESS_CONSULTANTS: 'business_consultants',
    INSURANCE_ADVISORS: 'insurance_advisors',
    INVESTMENT_ADVISORS: 'investment_advisors',
    RETIREMENT_PLANNERS: 'retirement_planners',
    DEBT_COUNSELORS: 'debt_counselors',
    
    // Legal Services
    LEGAL_ADVISORS: 'legal_advisors',
    IMMIGRATION_LAWYERS: 'immigration_lawyers',
    REAL_ESTATE_ATTORNEYS: 'real_estate_attorneys',
    FAMILY_LAWYERS: 'family_lawyers',
    CRIMINAL_DEFENSE_LAWYERS: 'criminal_defense_lawyers',
    CORPORATE_LAWYERS: 'corporate_lawyers',
    
    // Career & Employment
    EMPLOYMENT_SOLUTIONS: 'employment_solutions',
    CAREER_COACHES: 'career_coaches',
    HR_CONSULTANTS: 'hr_consultants',
    RESUME_EXPERTS: 'resume_experts',
    EXECUTIVE_RECRUITERS: 'executive_recruiters',
    
    // Health & Wellness
    PSYCHOTHERAPY: 'psychotherapy',
    NUTRITIONISTS: 'nutritionists',
    FITNESS_TRAINERS: 'fitness_trainers',
    MEDICAL_CONSULTANTS: 'medical_consultants',
    ADDICTION_COUNSELORS: 'addiction_counselors',
    
    // Travel & Lifestyle
    TRAVEL_AGENCIES: 'travel_agencies',
    TRAVEL_ADVISORS: 'travel_advisors',
    EVENT_PLANNERS: 'event_planners',
    PERSONAL_STYLISTS: 'personal_stylists',
    
    // Personal Development
    LIFE_COACHES: 'life_coaches',
    RELATIONSHIP_COUNSELORS: 'relationship_counselors',
    EDUCATIONAL_CONSULTANTS: 'educational_consultants',
    SPIRITUAL_ADVISORS: 'spiritual_advisors',
    
    // Property & Real Estate
    REAL_ESTATE_AGENTS: 'real_estate_agents',
    INTERIOR_DESIGNERS: 'interior_designers',
    HOME_IMPROVEMENT_CONSULTANTS: 'home_improvement_consultants',
    PROPERTY_MANAGERS: 'property_managers',
    
    // Technology & Digital
    IT_CONSULTANTS: 'it_consultants',
    DIGITAL_MARKETING_EXPERTS: 'digital_marketing_experts',
    WEB_DEVELOPMENT_CONSULTANTS: 'web_development_consultants',
    SOFTWARE_CONSULTANTS: 'software_consultants',
    
    // Specialized Services
    SHOPPING_ADVISORS: 'shopping_advisors',
    AUTOMOTIVE_CONSULTANTS: 'automotive_consultants',
    PET_CARE_ADVISORS: 'pet_care_advisors',
    SENIOR_CARE_CONSULTANTS: 'senior_care_consultants',
    ENVIRONMENTAL_CONSULTANTS: 'environmental_consultants'
};

// Template mapping with all template files
const templateMap = {
    // Existing templates
    [TEMPLATE_CATEGORIES.FINANCIAL]: financeTemplates,
    [TEMPLATE_CATEGORIES.PERSONAL]: personalTemplates,
    [TEMPLATE_CATEGORIES.HEALTH]: healthTemplates,
    [TEMPLATE_CATEGORIES.SHOPPING]: shoppingTemplates,
    [TEMPLATE_CATEGORIES.STARTUP]: require('../../../Data/startupTemplateSearch.json'),
    [TEMPLATE_CATEGORIES.IMMIGRATION]: immigrationTemplates,
    
    // New template files
    [TEMPLATE_CATEGORIES.FINANCIAL_ADVISORS]: financialAdvisorsTemplates,
    [TEMPLATE_CATEGORIES.ACCOUNTANTS]: accountantsTemplates,
    [TEMPLATE_CATEGORIES.TAX_ADVISORS]: taxAdvisorsTemplates,
    [TEMPLATE_CATEGORIES.BUSINESS_CONSULTANTS]: businessConsultantsTemplates,
    [TEMPLATE_CATEGORIES.LEGAL_ADVISORS]: legalAdvisorsTemplates,
    [TEMPLATE_CATEGORIES.CAREER_COACHES]: careerCoachesTemplates,
    [TEMPLATE_CATEGORIES.TRAVEL_ADVISORS]: travelAdvisorsTemplates,
    [TEMPLATE_CATEGORIES.IT_CONSULTANTS]: itConsultantsTemplates,
    [TEMPLATE_CATEGORIES.REAL_ESTATE_AGENTS]: realEstateAgentsTemplates,
    
    // For categories without specific templates yet, use generic fallbacks
    [TEMPLATE_CATEGORIES.INSURANCE_ADVISORS]: financialAdvisorsTemplates,
    [TEMPLATE_CATEGORIES.INVESTMENT_ADVISORS]: financialAdvisorsTemplates,
    [TEMPLATE_CATEGORIES.RETIREMENT_PLANNERS]: financialAdvisorsTemplates,
    [TEMPLATE_CATEGORIES.DEBT_COUNSELORS]: financialAdvisorsTemplates,
    [TEMPLATE_CATEGORIES.IMMIGRATION_LAWYERS]: legalAdvisorsTemplates,
    [TEMPLATE_CATEGORIES.REAL_ESTATE_ATTORNEYS]: legalAdvisorsTemplates,
    [TEMPLATE_CATEGORIES.FAMILY_LAWYERS]: legalAdvisorsTemplates,
    [TEMPLATE_CATEGORIES.CRIMINAL_DEFENSE_LAWYERS]: legalAdvisorsTemplates,
    [TEMPLATE_CATEGORIES.CORPORATE_LAWYERS]: legalAdvisorsTemplates,
    [TEMPLATE_CATEGORIES.EMPLOYMENT_SOLUTIONS]: careerCoachesTemplates,
    [TEMPLATE_CATEGORIES.HR_CONSULTANTS]: careerCoachesTemplates,
    [TEMPLATE_CATEGORIES.RESUME_EXPERTS]: careerCoachesTemplates,
    [TEMPLATE_CATEGORIES.EXECUTIVE_RECRUITERS]: careerCoachesTemplates,
    [TEMPLATE_CATEGORIES.PSYCHOTHERAPY]: healthTemplates,
    [TEMPLATE_CATEGORIES.NUTRITIONISTS]: healthTemplates,
    [TEMPLATE_CATEGORIES.FITNESS_TRAINERS]: healthTemplates,
    [TEMPLATE_CATEGORIES.MEDICAL_CONSULTANTS]: healthTemplates,
    [TEMPLATE_CATEGORIES.ADDICTION_COUNSELORS]: healthTemplates,
    [TEMPLATE_CATEGORIES.TRAVEL_AGENCIES]: travelAdvisorsTemplates,
    [TEMPLATE_CATEGORIES.EVENT_PLANNERS]: travelAdvisorsTemplates,
    [TEMPLATE_CATEGORIES.PERSONAL_STYLISTS]: personalTemplates,
    [TEMPLATE_CATEGORIES.LIFE_COACHES]: personalTemplates,
    [TEMPLATE_CATEGORIES.RELATIONSHIP_COUNSELORS]: personalTemplates,
    [TEMPLATE_CATEGORIES.EDUCATIONAL_CONSULTANTS]: personalTemplates,
    [TEMPLATE_CATEGORIES.SPIRITUAL_ADVISORS]: personalTemplates,
    [TEMPLATE_CATEGORIES.INTERIOR_DESIGNERS]: realEstateAgentsTemplates,
    [TEMPLATE_CATEGORIES.HOME_IMPROVEMENT_CONSULTANTS]: realEstateAgentsTemplates,
    [TEMPLATE_CATEGORIES.PROPERTY_MANAGERS]: realEstateAgentsTemplates,
    [TEMPLATE_CATEGORIES.DIGITAL_MARKETING_EXPERTS]: itConsultantsTemplates,
    [TEMPLATE_CATEGORIES.WEB_DEVELOPMENT_CONSULTANTS]: itConsultantsTemplates,
    [TEMPLATE_CATEGORIES.SOFTWARE_CONSULTANTS]: itConsultantsTemplates,
    [TEMPLATE_CATEGORIES.SHOPPING_ADVISORS]: shoppingTemplates,
    [TEMPLATE_CATEGORIES.AUTOMOTIVE_CONSULTANTS]: businessConsultantsTemplates,
    [TEMPLATE_CATEGORIES.PET_CARE_ADVISORS]: healthTemplates,
    [TEMPLATE_CATEGORIES.SENIOR_CARE_CONSULTANTS]: healthTemplates,
    [TEMPLATE_CATEGORIES.ENVIRONMENTAL_CONSULTANTS]: businessConsultantsTemplates,
};

// Current taxonomy (data/advisoryTaxonomy.ts): userCategories holds the category LABEL, which the
// legacy matching below does not recognise, so every modern category used to fall back to finance.
// Each slug maps to the closest existing template set.
const TAXONOMY_TEMPLATES = {
    'immigration': immigrationTemplates,
    'legal': legalAdvisorsTemplates,
    'financial': financialAdvisorsTemplates,
    'accounting-tax': accountantsTemplates,
    'insurance': financialAdvisorsTemplates,
    'real-estate': realEstateAgentsTemplates,
    'business-consulting': businessConsultantsTemplates,
    'career-employment': careerCoachesTemplates,
    'health-wellness': healthTemplates,
    'coaching-personal-development': personalTemplates,
    'education-academic': personalTemplates,
    'technology-digital': itConsultantsTemplates,
    'marketing-creative': businessConsultantsTemplates,
    'travel-hospitality': travelAdvisorsTemplates,
    'lifestyle-personal-services': personalTemplates,
    'environmental-sustainability': businessConsultantsTemplates,
    'family-relationships': personalTemplates,
    'pets-veterinary': healthTemplates,
    'mental-health': healthTemplates,
    'home-renovation': realEstateAgentsTemplates,
    'housing-tenancy': realEstateAgentsTemplates,
    'hr-employers': businessConsultantsTemplates,
    'automotive': shoppingTemplates,
    'tech-support': itConsultantsTemplates,
    'benefits-social-rights': legalAdvisorsTemplates,
    'consumer-rights': shoppingTemplates,
    'nutrition-fitness': healthTemplates,
    'addiction-recovery': healthTemplates,
    'eldercare-aging': healthTemplates,
    'pregnancy-parenthood': healthTemplates,
    'end-of-life': personalTemplates,
    'disability-accessibility': healthTemplates,
    'veterans-military': legalAdvisorsTemplates,
    'freelance-self-employment': businessConsultantsTemplates,
    'ecommerce-online-selling': businessConsultantsTemplates,
    'moving-relocation': travelAdvisorsTemplates,
    'newcomer-settlement': personalTemplates,
    'workplace-rights': legalAdvisorsTemplates,
    'healthcare-access': healthTemplates,
    'personal-budgeting': financeTemplates,
    'childcare-early-years': personalTemplates,
    'womens-health-menopause': healthTemplates,
    'condo-hoa-neighbours': realEstateAgentsTemplates,
    'identity-privacy': itConsultantsTemplates,
    'creators-influencers': businessConsultantsTemplates,
    'grants-funding': businessConsultantsTemplates,
    'startups-fundraising': businessConsultantsTemplates,
    'study-abroad-admissions': personalTemplates,
    'exam-prep': personalTemplates,
    'ai-automation-business': itConsultantsTemplates,
    'weddings-celebrations': travelAdvisorsTemplates,
    'restaurant-food-service': businessConsultantsTemplates,
    'short-term-rentals': realEstateAgentsTemplates,
    'small-business-buying-selling': businessConsultantsTemplates,
    'construction-contractor-business': businessConsultantsTemplates,
    'online-courses-coaching-business': businessConsultantsTemplates,
    'home-services-business': businessConsultantsTemplates,
    'beauty-salon-spa-business': businessConsultantsTemplates,
    'trucking-freight': businessConsultantsTemplates,
    'import-export-customs': businessConsultantsTemplates,
    'product-development-manufacturing': businessConsultantsTemplates,
    'other': personalTemplates,
};

/** Templates for a stored category: taxonomy slug/label first, then the legacy label matching. */
const templatesForCategory = (cat) => {
    const slug = findCategory(FALLBACK_TAXONOMY, cat)?.slug;
    if (slug && TAXONOMY_TEMPLATES[slug]) return TAXONOMY_TEMPLATES[slug];
    return templateMap[normalizeCategory(cat)] || financeTemplates;
};

// Helper to convert various backend category strings to internal keys
const normalizeCategory = (cat) => {
    if (!cat) return TEMPLATE_CATEGORIES.FINANCIAL;
    const lower = cat.toLowerCase();
    
    // Existing categories
    if (lower.includes('financial adviser')) return TEMPLATE_CATEGORIES.FINANCIAL;
    if (lower.includes('personal development adviser')) return TEMPLATE_CATEGORIES.PERSONAL;
    if (lower.includes('health and wellness adviser')) return TEMPLATE_CATEGORIES.HEALTH;
    if (lower.includes('shopping adviser')) return TEMPLATE_CATEGORIES.SHOPPING;
    if (lower.includes('startup adviser')) return TEMPLATE_CATEGORIES.STARTUP;
    if (lower.includes('immigration adviser')) return TEMPLATE_CATEGORIES.IMMIGRATION;
    
    // New Financial & Business categories
    if (lower.includes('financial advisors')) return TEMPLATE_CATEGORIES.FINANCIAL_ADVISORS;
    if (lower.includes('accountants') || lower.includes('cpas')) return TEMPLATE_CATEGORIES.ACCOUNTANTS;
    if (lower.includes('tax advisors')) return TEMPLATE_CATEGORIES.TAX_ADVISORS;
    if (lower.includes('business consultants')) return TEMPLATE_CATEGORIES.BUSINESS_CONSULTANTS;
    if (lower.includes('insurance advisors')) return TEMPLATE_CATEGORIES.INSURANCE_ADVISORS;
    if (lower.includes('investment advisors')) return TEMPLATE_CATEGORIES.INVESTMENT_ADVISORS;
    if (lower.includes('retirement planners')) return TEMPLATE_CATEGORIES.RETIREMENT_PLANNERS;
    if (lower.includes('debt counselors')) return TEMPLATE_CATEGORIES.DEBT_COUNSELORS;
    
    // Legal Services
    if (lower.includes('lawyers') || lower.includes('legal advisors')) return TEMPLATE_CATEGORIES.LEGAL_ADVISORS;
    if (lower.includes('immigration lawyers')) return TEMPLATE_CATEGORIES.IMMIGRATION_LAWYERS;
    if (lower.includes('real estate attorneys')) return TEMPLATE_CATEGORIES.REAL_ESTATE_ATTORNEYS;
    if (lower.includes('family lawyers')) return TEMPLATE_CATEGORIES.FAMILY_LAWYERS;
    if (lower.includes('criminal defense lawyers')) return TEMPLATE_CATEGORIES.CRIMINAL_DEFENSE_LAWYERS;
    if (lower.includes('corporate lawyers')) return TEMPLATE_CATEGORIES.CORPORATE_LAWYERS;
    
    // Career & Employment
    if (lower.includes('employment solutions')) return TEMPLATE_CATEGORIES.EMPLOYMENT_SOLUTIONS;
    if (lower.includes('career coaches')) return TEMPLATE_CATEGORIES.CAREER_COACHES;
    if (lower.includes('hr consultants')) return TEMPLATE_CATEGORIES.HR_CONSULTANTS;
    if (lower.includes('resume') || lower.includes('linkedin experts')) return TEMPLATE_CATEGORIES.RESUME_EXPERTS;
    if (lower.includes('executive recruiters')) return TEMPLATE_CATEGORIES.EXECUTIVE_RECRUITERS;
    
    // Health & Wellness
    if (lower.includes('psychotherapy') || lower.includes('mental health')) return TEMPLATE_CATEGORIES.PSYCHOTHERAPY;
    if (lower.includes('nutritionists') || lower.includes('dietitians')) return TEMPLATE_CATEGORIES.NUTRITIONISTS;
    if (lower.includes('fitness trainers')) return TEMPLATE_CATEGORIES.FITNESS_TRAINERS;
    if (lower.includes('medical consultants')) return TEMPLATE_CATEGORIES.MEDICAL_CONSULTANTS;
    if (lower.includes('addiction counselors')) return TEMPLATE_CATEGORIES.ADDICTION_COUNSELORS;
    
    // Travel & Lifestyle
    if (lower.includes('travel agencies')) return TEMPLATE_CATEGORIES.TRAVEL_AGENCIES;
    if (lower.includes('travel advisors')) return TEMPLATE_CATEGORIES.TRAVEL_ADVISORS;
    if (lower.includes('event planners')) return TEMPLATE_CATEGORIES.EVENT_PLANNERS;
    if (lower.includes('personal stylists')) return TEMPLATE_CATEGORIES.PERSONAL_STYLISTS;
    
    // Personal Development
    if (lower.includes('life coaches')) return TEMPLATE_CATEGORIES.LIFE_COACHES;
    if (lower.includes('relationship counselors')) return TEMPLATE_CATEGORIES.RELATIONSHIP_COUNSELORS;
    if (lower.includes('educational consultants')) return TEMPLATE_CATEGORIES.EDUCATIONAL_CONSULTANTS;
    if (lower.includes('spiritual advisors')) return TEMPLATE_CATEGORIES.SPIRITUAL_ADVISORS;
    
    // Property & Real Estate
    if (lower.includes('real estate agents')) return TEMPLATE_CATEGORIES.REAL_ESTATE_AGENTS;
    if (lower.includes('interior designers')) return TEMPLATE_CATEGORIES.INTERIOR_DESIGNERS;
    if (lower.includes('home improvement consultants')) return TEMPLATE_CATEGORIES.HOME_IMPROVEMENT_CONSULTANTS;
    if (lower.includes('property managers')) return TEMPLATE_CATEGORIES.PROPERTY_MANAGERS;
    
    // Technology & Digital
    if (lower.includes('it consultants')) return TEMPLATE_CATEGORIES.IT_CONSULTANTS;
    if (lower.includes('digital marketing experts')) return TEMPLATE_CATEGORIES.DIGITAL_MARKETING_EXPERTS;
    if (lower.includes('web development consultants')) return TEMPLATE_CATEGORIES.WEB_DEVELOPMENT_CONSULTANTS;
    if (lower.includes('software consultants')) return TEMPLATE_CATEGORIES.SOFTWARE_CONSULTANTS;
    
    // Specialized Services
    if (lower.includes('shopping/purchase advisors')) return TEMPLATE_CATEGORIES.SHOPPING_ADVISORS;
    if (lower.includes('automotive consultants')) return TEMPLATE_CATEGORIES.AUTOMOTIVE_CONSULTANTS;
    if (lower.includes('pet care advisors')) return TEMPLATE_CATEGORIES.PET_CARE_ADVISORS;
    if (lower.includes('senior care consultants')) return TEMPLATE_CATEGORIES.SENIOR_CARE_CONSULTANTS;
    if (lower.includes('environmental consultants')) return TEMPLATE_CATEGORIES.ENVIRONMENTAL_CONSULTANTS;
    
    return TEMPLATE_CATEGORIES.FINANCIAL; // default fallback
};

const IconWrapper = styled.div`
    width: 20px;
    height: 30px;
    background-color: transparent;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    pointer-events: auto;

    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

`;

const Message = styled.input`
    border: none;
    background-color: transparent;
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    font-style: normal;
    font-weight: 300;
    color: #E2E8F0;
    flex-grow: 1;
    height: 100%;
    width: 90%;
    outline: none;

    &::placeholder {
        color: #A0AEC0;
    }

    &:focus {
        background-color: transparent;
        outline: none;
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0px 1000px transparent inset;
        transition: background-color 5000s ease-in-out 0s;
    }
`;

const SendMessageWrapper = styled.div`
    display: flex;
    align-items: center;
    flex-grow: 1;
    height: 100%;
    position: relative;
    padding: 0 10px;
`;

const MessageBubble = styled.div`
    background: ${({ user }) => (user 
        ? 'linear-gradient(145deg, #2C5282 0%, #1E3A8A 100%)'  // Royal blue for user
        : 'linear-gradient(145deg, #1a1f2e 0%, #252b3d 100%)'  // Dark elegant for bot
    )};
    color: ${({ user }) => (user ? '#BEE3F8' : '#e8eaed')};
    border-radius: 16px;
    padding: 14px 18px;
    margin: 6px 8px;
    max-width: 85%;
    font-size: 13px;
    line-height: 1.7;
    text-align: left;
    word-wrap: break-word;
    user-select: text;
    cursor: text;
    box-shadow: ${({ user }) => (user 
        ? '0 3px 10px rgba(30, 58, 138, 0.3)' 
        : '0 3px 12px rgba(0, 0, 0, 0.4)'
    )};
    border: 1px solid ${({ user }) => (user ? 'rgba(190, 227, 248, 0.2)' : 'rgba(99, 102, 241, 0.2)')};

    /* Paragraphs */
    p {
        margin: 0 0 10px 0;
        &:last-child {
            margin-bottom: 0;
        }
    }

    /* Lists */
    ul, ol {
        margin: 10px 0;
        padding-left: 22px;
    }

    li {
        margin: 6px 0;
        line-height: 1.6;
    }

    /* Headers */
    h1, h2, h3, h4 {
        color: #a5b4fc;
        margin: 14px 0 8px 0;
        font-weight: 600;
    }

    /* Bold text */
    strong {
        color: ${({ user }) => (user ? '#fff' : '#a5b4fc')};
        font-weight: 600;
    }

    /* Code */
    code {
        background: rgba(99, 102, 241, 0.2);
        color: #a5b4fc;
        padding: 2px 6px;
        border-radius: 4px;
        font-family: 'Fira Code', 'Consolas', monospace;
        font-size: 0.9em;
    }

    /* Code blocks */
    pre {
        background: rgba(0, 0, 0, 0.3);
        border: 1px solid rgba(99, 102, 241, 0.2);
        border-radius: 8px;
        padding: 12px;
        overflow-x: auto;
        margin: 10px 0;
        
        code {
            background: none;
            padding: 0;
        }
    }

    /* Blockquotes */
    blockquote {
        border-left: 3px solid #6366f1;
        margin: 10px 0;
        padding-left: 12px;
        color: rgba(255, 255, 255, 0.8);
        font-style: italic;
    }

    /* Horizontal rule */
    hr {
        border: none;
        height: 1px;
        background: linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.4), transparent);
        margin: 12px 0;
    }

    /* Links */
    a {
        color: #818cf8;
        text-decoration: none;
        &:hover {
            text-decoration: underline;
        }
    }
`;

const ChatbotSenderName = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${({ user }) => (user 
        ? 'rgba(255, 255, 255, 0.95)'  // High contrast white for user messages (blue bubble)
        : 'rgba(255, 255, 255, 0.95)'  // High contrast white for bot messages (gray bubble)
    )};
    margin-bottom: 6px;
    opacity: 1;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
    font-weight: 700;
`;

const MessageBubbleWrapper = styled.div`
    display: flex;
    justify-content: ${({ user }) => (user ? 'flex-end' : 'flex-start')};
    margin: 4px 0;
`;

const LoadingBubble = styled.div`
    background: linear-gradient(145deg, #4A5568 0%, #2D3748 100%);  // Match bot message color
    border-radius: 12px;
    padding: 8px 12px;
    margin: 4px 8px;
    max-width: 70%;
    font-size: 14px;
    font-style: italic;
    line-height: 1.5;
    color: #E2E8F0;  // Match bot text color
    text-align: left;
    user-select: text;
    cursor: text;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const SuggestionsContainer = styled.div`
    position: absolute;
    bottom: 100%;
    left: 0;
    right: 0;
    background-color: #1A202C;
    border: 1px solid rgba(226, 232, 240, 0.1);
    border-radius: 8px;
    margin-bottom: 8px;
    max-height: 350px;
    overflow-y: auto;
    z-index: 1000;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
`;

const SuggestionItem = styled.div`
    padding: 8px 12px 8px 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    color: #BEE3F8;
    transition: background-color 0.2s;
    font-size: 13px;
    border-left: 3px solid transparent;
    margin: 2px 0;

    &:hover {
        background-color: #2D3748;
        border-left: 3px solid #4299E1;
    }

    .category-icon {
        margin-right: 8px;
        opacity: 0.8;
    }
`;

const CategoryHeader = styled.div`
    padding: 10px 12px;
    color: #FFFFFF;
    font-weight: 600;
    background-color: #2C5282;
    display: flex;
    align-items: center;
    font-size: 14px;
    margin-top: 4px;
    letter-spacing: 0.5px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    text-transform: uppercase;

    &:first-child {
        margin-top: 0;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
    }

    .category-icon {
        margin-right: 8px;
        font-size: 16px;
    }
`;

const EmojiPickerContainer = styled.div`
    position: absolute;
    top: -455px;
    z-index: 1000000;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
    border: 1px solid #4A5568;
    overflow: hidden;
`;

const Chatbot = ({ threadId }) => {

    // const authenticatedAxios = useAuthenticateAxios();
    const { user, getAccessTokenSilently, isAuthenticated } = useAuth0();
    const { showToast } = useToast();

    const fileInputRef = useRef(null);
    const [convesarionId, setConversationId] = useState("");
    const [currentMessage, setCurrentMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [loadingDots, setLoadingDots] = useState('');
    const [messages, setMessages] = useState([]);
    const [emojiPickerOn, setEmojiPickerOn] = useState(false);
    const messagesEndRef = useRef(null);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Add state for user category
    const [userCategory, setUserCategory] = useState(TEMPLATE_CATEGORIES.FINANCIAL); // Default to financial for now
    const [activeTemplates, setActiveTemplates] = useState(financeTemplates);

    // Ref for the scrollable messages container
    const messagesContainerRef = useRef(null);

    // Parse markdown in bot responses for beautiful formatting
    const parseMarkdown = (text) => {
        if (!text) return '';
        
        const paragraphs = text.split(/\n\n+/);
        
        const parsedParagraphs = paragraphs.map(paragraph => {
            let p = paragraph.trim();
            if (!p) return '';
            
            // Horizontal rule
            if (/^[-*_]{3,}$/.test(p)) {
                return '<hr/>';
            }
            
            // Headers
            if (p.startsWith('### ')) {
                return `<h3>${p.slice(4)}</h3>`;
            }
            if (p.startsWith('## ')) {
                return `<h2>${p.slice(3)}</h2>`;
            }
            if (p.startsWith('# ')) {
                return `<h1>${p.slice(2)}</h1>`;
            }
            
            // Code blocks
            if (p.startsWith('```')) {
                const codeContent = p.replace(/```[\w]*\n?/g, '').replace(/```$/g, '');
                return `<pre><code>${codeContent}</code></pre>`;
            }
            
            // Lists
            const lines = p.split('\n');
            const isNumberedList = lines.every(line => /^\d+[\.\)]\s/.test(line.trim()) || line.trim() === '');
            const isBulletList = lines.every(line => /^[-•*]\s/.test(line.trim()) || line.trim() === '');
            
            if (isNumberedList && lines.some(line => /^\d+[\.\)]\s/.test(line.trim()))) {
                const items = lines
                    .filter(line => line.trim())
                    .map(line => {
                        const content = line.replace(/^\d+[\.\)]\s*/, '').trim();
                        const formatted = content
                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                            .replace(/\*(.*?)\*/g, '<em>$1</em>')
                            .replace(/`(.*?)`/g, '<code>$1</code>');
                        return `<li>${formatted}</li>`;
                    })
                    .join('');
                return `<ol>${items}</ol>`;
            }
            
            if (isBulletList && lines.some(line => /^[-•*]\s/.test(line.trim()))) {
                const items = lines
                    .filter(line => line.trim())
                    .map(line => {
                        const content = line.replace(/^[-•*]\s*/, '').trim();
                        const formatted = content
                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                            .replace(/\*(.*?)\*/g, '<em>$1</em>')
                            .replace(/`(.*?)`/g, '<code>$1</code>');
                        return `<li>${formatted}</li>`;
                    })
                    .join('');
                return `<ul>${items}</ul>`;
            }
            
            // Blockquote
            if (p.startsWith('>')) {
                const content = p.replace(/^>\s*/gm, '');
                return `<blockquote>${content}</blockquote>`;
            }
            
            // Regular paragraph with inline formatting
            let formatted = p
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                .replace(/`(.*?)`/g, '<code>$1</code>')
                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>')
                .replace(/\n/g, '<br/>');
            
            return `<p>${formatted}</p>`;
        });
        
        return parsedParagraphs.filter(p => p).join('');
    };

    useEffect(() => {
        let cancelled = false;
        const fetchData = async () => {
            try {
                if (isAuthenticated && user) {
                    const token = await getAccessTokenSilently();
                    const response = await axios.get(`${process.env.REACT_APP_SERVER}chatbot/getConversationId`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'x-thread-id': threadId
                        }
                    });

                    if (!cancelled) {
                        const res = response.data.convesationId;
                        setConversationId(res);
                    }
                }
            } catch (error) {
                console.log(error)
            }
        };
        fetchData();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [threadId, isAuthenticated]);

    useEffect(() => {
        if (isLoading) {
            const interval = setInterval(() => {
                setLoadingDots(prev => (prev.length < 5 ? prev + '.' : '.'));
            }, 500);
            return () => clearInterval(interval);
        }
    }, [isLoading]);

    useEffect(() => {
        let cancelled = false;
        const fetchUserCategory = async () => {
            try {
                if (isAuthenticated && user) {
                    const token = await getAccessTokenSilently();
                    const response = await axios.get(`${process.env.REACT_APP_SERVER}user/category`, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    if (cancelled) return;
                    const rawCategory = response.data.userCategory;
                    console.log("Received user category from API:", rawCategory);
                    setUserCategory(rawCategory);
                    setActiveTemplates(templatesForCategory(rawCategory));
                }
            } catch (error) {
                console.error('Error fetching user category:', error);
                if (error.response) {
                    console.error('Response data:', error.response.data);
                    console.error('Response status:', error.response.status);
                }
                if (!cancelled) {
                    setUserCategory(TEMPLATE_CATEGORIES.FINANCIAL);
                    setActiveTemplates(templateMap[TEMPLATE_CATEGORIES.FINANCIAL]);
                }
            }
        };

        fetchUserCategory();
        return () => { cancelled = true; };
    }, [isAuthenticated, user]);

    const sendMessage = async () => {
        if (currentMessage.length == 0)
            return;

        try {
            const token = await getAccessTokenSilently();
            setIsLoading(true);
            const tempMessage = currentMessage;
            setCurrentMessage("");
            setShowSuggestions(false);  // Hide suggestions when sending
            setSuggestions([]);         // Clear suggestions array
            let newMessagesLiest = [...messages, { text: tempMessage, user: true }];
            setMessages(newMessagesLiest);
            const response = await axios.post(`${process.env.REACT_APP_SERVER}chatbot`, { message: tempMessage }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-conversation-id': convesarionId
                }
            });
            const res = response.data.message;
            // Strip HTML tags and format the response text with line breaks
            const strippedText = stripHtml(res);
            const formattedText = strippedText.split('###').map(part => part.trim()).join('\n\n');
            newMessagesLiest.push({ text: formattedText, user: false });
            setIsLoading(false);
            setMessages(newMessagesLiest)

        } catch (error) {
            console.log(error)
        }
    };

    // Function to strip HTML tags from text
    const stripHtml = (html) => {
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || '';
    };

    const findSuggestions = (input) => {
        if (!input || !input.trim()) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const inputLower = input.toLowerCase();
        const matchedSuggestions = [];

        if (activeTemplates && activeTemplates.categories) {
            activeTemplates.categories.forEach(category => {
                const matchedQuestions = category.questions.filter(question =>
                    question.toLowerCase().includes(inputLower)
                );

                if (matchedQuestions.length > 0) {
                    matchedSuggestions.push({
                        category: category.name,
                        icon: category.icon,
                        questions: matchedQuestions
                    });
                }
            });
        }

        setSuggestions(matchedSuggestions);
        setShowSuggestions(matchedSuggestions.length > 0);
    };

    const onInputValueChange = (event) => {
        const value = event.target.value;
        setCurrentMessage(value);
        
        // If input is empty, hide suggestions
        if (!value.trim()) {
            setShowSuggestions(false);
            setSuggestions([]);
            return;
        }
        
        findSuggestions(value);
    };

    const selectSuggestion = (question) => {
        setCurrentMessage(question);
        setShowSuggestions(false);
        setSuggestions([]);
    };

    const openEmojiPicker = () => {
        setEmojiPickerOn(!emojiPickerOn)
    }

    const emojiSelected = (event) => {
        console.log(event);
        setCurrentMessage(currentMessage + event.native)
    }

    const clickUpload = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = async (event) => {
        const files = event.target.files;

        if (files.length == 0)
            return;

        const formData = new FormData();

        for (let i = 0; i < files.length; i++) {
            formData.append('files', files[i]);
        }

        setIsLoading(true);
        try {
            const token = await getAccessTokenSilently();

            const response = await axios.post(`${process.env.REACT_APP_SERVER}chatbot/upload`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-conversation-id': convesarionId,
                    'Content-Type': 'multipart/form-data',
                }
            });

            if (response.data.link) {
                setIsLoading(false);

                setCurrentMessage("");
                let newMessagesLiest = [...messages, { text: "file uploaded", user: true }];
                setMessages(newMessagesLiest);

            } else {
                console.log("error uploading file")
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            showToast('An error occurred while uploading the file', 'error');
        } finally {
            setIsLoading(false);
        }

    };

    // Scroll to bottom when messages change
    useEffect(() => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    }, [messages]);

    return (
        <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
            maxHeight: '100%'
        }}>
            <div
                ref={messagesContainerRef}
                style={{
                    position: 'absolute',  // Take it out of flow
                    top: 0,
                    bottom: '60px',  // Leave space for input area
                    left: 0,
                    right: 0,
                    overflowY: 'auto',
                    padding: '10px',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {messages.map((msg, index) => (
                    <MessageBubbleWrapper key={index} user={msg.user}>
                        <MessageBubble user={msg.user}>
                            <ChatbotSenderName user={msg.user}>
                                {msg.user ? 
                                    (() => {
                                        const userName = user?.name || user?.email || 'User';
                                        // Capitalize first letter of each word
                                        return userName.split(' ').map(word => {
                                            if (word.length === 0) return word;
                                            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                                        }).join(' ');
                                    })()
                                    : 'Eve Virtual Assistant'
                                }
                            </ChatbotSenderName>
                            {msg.user ? (
                                msg.text
                            ) : (
                                <div 
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(parseMarkdown(msg.text))
                                    }}
                                />
                            )}
                        </MessageBubble>
                    </MessageBubbleWrapper>
                ))}
                {isLoading && (
                    <MessageBubbleWrapper>
                        <LoadingBubble>
                            {loadingDots}
                        </LoadingBubble>
                    </MessageBubbleWrapper>
                )}
            </div>

            <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '60px',
                backgroundColor: 'inherit',
                padding: '10px',
                borderRadius: '8px 8px 8px 8px'
            }}>
                <div style={{
                    border: '#4A5568 1px solid',
                    height: '100%',
                    position: 'relative',
                    backgroundColor: '#2D3748',
                    overflow: 'visible',
                    borderRadius: '4px'
                }}>
                    <SendMessageWrapper>
                        {showSuggestions && suggestions.length > 0 && (
                            <SuggestionsContainer>
                                {suggestions.map((category, index) => (
                                    <div key={index}>
                                        <CategoryHeader>
                                            <span className="category-icon">{category.icon}</span>
                                            {category.category}
                                        </CategoryHeader>
                                        {category.questions.map((question, qIndex) => (
                                            <SuggestionItem
                                                key={qIndex}
                                                onClick={() => selectSuggestion(question)}
                                            >
                                                <span className="category-icon">•</span>
                                                {question}
                                            </SuggestionItem>
                                        ))}
                                    </div>
                                ))}
                            </SuggestionsContainer>
                        )}

                        {emojiPickerOn &&
                            <EmojiPickerContainer>
                                <Picker data={data} onEmojiSelect={emojiSelected} theme="dark" />
                            </EmojiPickerContainer>
                        }

                        <Message
                            id="message-input"
                            type="text"
                            placeholder="Type your message..."
                            value={currentMessage}
                            onChange={onInputValueChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !isLoading) {
                                    if (showSuggestions) {
                                        e.preventDefault();
                                        if (suggestions.length > 0 && suggestions[0].questions.length > 0) {
                                            selectSuggestion(suggestions[0].questions[0]);
                                        }
                                    } else {
                                        sendMessage();
                                    }
                                }
                            }}
                        />

                        <IconWrapper id="send-button" onClick={sendMessage} disabled={isLoading}>
                            <img src="https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/send-icon.png" alt="send-icon" />
                        </IconWrapper>
                    </SendMessageWrapper>
                </div>
            </div>
        </div>
    )
}

export default Chatbot;